import React, { useState, useMemo, useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import * as transactionService from "@/shared/api/services/transactionService";
import * as engineService from "@/shared/api/services/engineService";
import { motion } from "framer-motion";
import { format } from "date-fns";
import { Button } from "@/shared/components/ui/button";
import { Input } from "@/shared/components/ui/input";
import { Badge } from "@/shared/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/shared/components/ui/select";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from "@/shared/components/ui/sheet";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/shared/components/ui/popover";
import { Calendar } from "@/shared/components/ui/calendar";
import {
  Search,
  Calendar as CalendarIcon,
  ArrowDownToLine,
  ArrowUpFromLine,
  Copy,
  Check,
  CheckCircle2,
  XCircle,
  Clock,
  Loader2,
  X,
  Download,
  Webhook,
  ChevronLeft,
  ChevronRight,
  RefreshCw,
} from "lucide-react";
import EngineLayout from "@/shared/components/engine/EngineLayout";
import { useLanguage } from "@/shared/components/shared/LanguageContext";
import { mapGlobalTransactions } from "@/shared/utils/transactionMappers";

export default function EngineTransactions() {
  const { t, language } = useLanguage();

  const statusConfig = useMemo(() => ({
    success: { icon: CheckCircle2, color: "bg-emerald-100 text-emerald-700", label: t("success") },
    pending: { icon: Clock, color: "bg-amber-100 text-amber-700", label: t("pending") },
    processing: { icon: Loader2, color: "bg-blue-100 text-blue-700", label: t("processing") },
    failed: { icon: XCircle, color: "bg-red-100 text-red-700", label: t("declined") || "Declined" },
    declined: { icon: XCircle, color: "bg-red-100 text-red-700", label: t("declined") || "Declined" },
    cancelled: { icon: XCircle, color: "bg-slate-100 text-slate-700", label: t("cancelled") || "Cancelled" },
    expired: { icon: XCircle, color: "bg-slate-100 text-slate-700", label: t("expired") },
  }), [t, language]);

  const [search, setSearch] = useState("");
  const [page, setPage] = useState(0);
  const pageSize = 10;
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [selectedTx, setSelectedTx] = useState(null);
  const [copiedId, setCopiedId] = useState(null);
  const [merchantIds, setMerchantIds] = useState([]);

  const [filters, setFilters] = useState({
    merchant: "all",
    status: "all",
    type: "all",
    currency: "all",
    dateFrom: null,
    dateTo: null,
  });

  const resellerName = "Gaming Partners Ltd";

  // Fetch All Merchants (Engine's + Resellers')
  const { data: allMerchants = [], isLoading: merchantsLoading } = useQuery({
    queryKey: ["engine-all-merchants"],
    queryFn: async () => {
      let allMerchantIdes = [];
      let allMerchantsList = [];
      
      // 1. Get all merchants created by engine logged in user
      const ownedMerchants = await engineService.getMyMerchants();
      ownedMerchants.forEach(m => {
        allMerchantIdes.push(m.id);
        allMerchantsList.push(m);
      });
      
      // 2. Get all the resellers created by engine logged in user
      const resellers = await engineService.getMyResellers();
      const resellerIdes = resellers.map(r => r.id);
      
      // 3. Then one by one get all the merchant ides created by resellers using resellerIdes
      for (const rId of resellerIdes) {
        const resellerMerchants = await engineService.getResellerMerchants(rId);
        resellerMerchants.forEach(m => {
          if (!allMerchantIdes.includes(m.id)) {
            allMerchantIdes.push(m.id);
            allMerchantsList.push(m);
          }
        });
      }
      
      return allMerchantsList;
    },
    refetchInterval: 60000,
  });

  // Update merchantIds based on selection or all owned merchants
  useEffect(() => {
    if (filters.merchant !== "all") {
      setMerchantIds([filters.merchant]);
    } else if (allMerchants.length > 0) {
      setMerchantIds(allMerchants.map(m => m.id));
    } else {
      // Don't set to NO_MERCHANT_FOUND if we are still loading merchants
      if (!merchantsLoading) {
        setMerchantIds(["NO_MERCHANT_FOUND"]);
      }
    }
  }, [filters.merchant, allMerchants, merchantsLoading]);

  const { data: paginationResult, isLoading: transactionsLoading, refetch, error } = useQuery({
    queryKey: ["engine-transactions-paginated", page, pageSize, search, filters, merchantIds],
    queryFn: () => {
      const backendFilters = {};
      if (filters.status !== "all") backendFilters.status = filters.status;
      if (filters.type !== "all") backendFilters.type = filters.type;
      if (filters.currency !== "all") backendFilters.currency = filters.currency;
      if (filters.dateFrom) backendFilters.startDate = filters.dateFrom.toISOString();
      if (filters.dateTo) {
        const endOfToDay = new Date(filters.dateTo);
        endOfToDay.setHours(23, 59, 59, 999);
        backendFilters.endDate = endOfToDay.toISOString();
      }

      return transactionService.getPaginatedTransactions(
        page + 1,
        pageSize,
        search,
        backendFilters,
        merchantIds
      );
    },
    keepPreviousData: true,
    enabled: merchantIds.length > 0,
  });

  const isLoading = transactionsLoading || (merchantsLoading && !merchantIds.length);

  const rawTransactions = paginationResult?.data || [];
  const totalTransactions = paginationResult?.total || 0;
  const totalPages = paginationResult?.totalPages || 1;

  const transactions = useMemo(() => {
    return mapGlobalTransactions(rawTransactions);
  }, [rawTransactions]);

  const copyToClipboard = (e, id) => {
    e.stopPropagation();
    navigator.clipboard.writeText(id);
    setCopiedId(id);
    setTimeout(() => setCopiedId(null), 2000);
  };

  const handleRefresh = async () => {
    setIsRefreshing(true);
    try {
      await refetch();
    } catch (error) {
      console.error('Error refreshing data:', error);
    } finally {
      setTimeout(() => setIsRefreshing(false), 500);
    }
  };

  const handleExport = () => {
    const headers = [t("type"), t("paymentId"), t("reference"), t("merchant"), t("amount"), t("status"), t("date")];
    const rows = transactions.map((tx) => [
      tx.type === "withdrawal" ? t("withdrawal") : t("deposit"),
      tx.id,
      tx.providerTransactionId || "",
      tx.merchant?.name || tx.merchant?.code || "",
      (tx.amount ?? 0).toString(),
      tx.status,
      tx.createdAt,
    ]);
    const csv = [headers.join(","), ...rows.map((r) => r.join(","))].join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `engine-transactions-${new Date().toISOString().split("T")[0]}.csv`;
    a.click();
  };

  const clearFilters = () => {
    setFilters({
      merchant: "all",
      status: "all",
      type: "all",
      currency: "all",
      dateFrom: null,
      dateTo: null,
    });
    setSearch("");
    setPage(0);
  };

  const hasFilters = search || filters.status !== "all" || filters.type !== "all" || filters.merchant !== "all" || filters.dateFrom;

  const customDateRangeLabel = (filters.dateFrom || filters.dateTo)
    ? `${filters.dateFrom ? format(filters.dateFrom, "MMM d, yyyy") : "Start"} - ${filters.dateTo ? format(filters.dateTo, "MMM d, yyyy") : "End"}`
    : null;

  const TransactionRowSkeleton = () => (
    <tr className="animate-pulse">
      <td className="px-4 py-4">
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-lg bg-slate-100" />
          <div className="space-y-2">
            <div className="h-4 w-24 bg-slate-100 rounded" />
            <div className="h-3 w-16 bg-slate-50 rounded" />
          </div>
        </div>
      </td>
      <td className="px-4 py-4"><div className="h-4 w-24 bg-slate-50 rounded" /></td>
      <td className="px-4 py-4"><div className="h-4 w-16 bg-slate-50 rounded" /></td>
      <td className="px-4 py-4"><div className="h-4 w-20 bg-slate-50 rounded ml-auto" /></td>
      <td className="px-4 py-4"><div className="h-4 w-16 bg-slate-50 rounded ml-auto" /></td>
      <td className="px-4 py-4"><div className="h-4 w-20 bg-slate-50 rounded ml-auto" /></td>
      <td className="px-4 py-4"><div className="h-7 w-20 bg-slate-50 rounded-full" /></td>
      <td className="px-4 py-4"><div className="h-4 w-24 bg-slate-50 rounded" /></td>
    </tr>
  );

  return (
    <EngineLayout currentPage="EngineTransactions" resellerName={resellerName}>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-slate-900">{t("transactions")}</h1>
            <p className="text-slate-500">
              {isLoading ? t("loading") : `${totalTransactions.toLocaleString()} ${t("transactions")}`}
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" onClick={handleRefresh} disabled={isRefreshing} className="gap-2">
              <RefreshCw className={`h-4 w-4 ${isRefreshing ? 'animate-spin' : ''}`} />
              {t("refresh")}
            </Button>
            <Button variant="outline" onClick={handleExport} className="gap-2">
              <Download className="h-4 w-4" />
              {t("exportCsv")}
            </Button>
          </div>
        </div>

        {/* Filters */}
        <div className="bg-white rounded-2xl p-4 shadow-sm border border-slate-100 space-y-4">
          <div className="flex flex-wrap gap-3">
            <div className="relative flex-1 min-w-[200px]">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
              <Input
                placeholder={t("searchHeaderPlaceholder")}
                value={search}
                onChange={(e) => { setSearch(e.target.value); setPage(0); }}
                className="pl-10"
              />
            </div>

            <Select value={filters.merchant} onValueChange={(v) => { setFilters({ ...filters, merchant: v }); setPage(0); }}>
              <SelectTrigger className="w-[160px]">
                <SelectValue placeholder={t("merchant")} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">{t("allMerchants")}</SelectItem>
                {allMerchants.map(m => (
                  <SelectItem key={m.id} value={m.id}>{m.name || m.code}</SelectItem>
                ))}
              </SelectContent>
            </Select>

            <Select value={filters.status} onValueChange={(v) => { setFilters({ ...filters, status: v }); setPage(0); }}>
              <SelectTrigger className="w-[140px]">
                <SelectValue placeholder={t("status")} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">{t("allStatus")}</SelectItem>
                <SelectItem value="pending">{t("pending")}</SelectItem>
                <SelectItem value="processing">{t("processing")}</SelectItem>
                <SelectItem value="success">{t("success")}</SelectItem>
                <SelectItem value="declined">{t("declined") || "Declined"}</SelectItem>
              </SelectContent>
            </Select>

            <Select value={filters.type} onValueChange={(v) => { setFilters({ ...filters, type: v }); setPage(0); }}>
              <SelectTrigger className="w-[140px]">
                <SelectValue placeholder={t("type")} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">{t("allTypes")}</SelectItem>
                <SelectItem value="deposit">{t("deposits")}</SelectItem>
                <SelectItem value="withdrawal">{t("withdrawals")}</SelectItem>
              </SelectContent>
            </Select>

            <Popover>
              <PopoverTrigger asChild>
                <Button variant="outline" className="gap-2">
                  <CalendarIcon className="h-4 w-4" />
                  {customDateRangeLabel || t("dateRange")}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0" align="start">
                <Calendar
                  mode="range"
                  selected={{ from: filters.dateFrom, to: filters.dateTo }}
                  onSelect={(range) => {
                    setFilters({ ...filters, dateFrom: range?.from || null, dateTo: range?.to || null });
                    setPage(0);
                  }}
                  numberOfMonths={2}
                />
              </PopoverContent>
            </Popover>

            {hasFilters && (
              <Button variant="ghost" onClick={clearFilters} className="text-slate-500">
                <X className="h-4 w-4 mr-1" />
                {t("clearFilters")}
              </Button>
            )}
          </div>
        </div>

        {/* Transactions Table */}
        <div className="bg-white rounded-2xl shadow-sm border border-slate-100 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="bg-slate-50 border-b border-slate-100">
                  <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase">{t("transaction")}</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase">{t("merchant")}</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase">{t("type")}</th>
                  <th className="text-right px-4 py-3 text-xs font-semibold text-slate-500 uppercase">{t("amount")}</th>
                  <th className="text-right px-4 py-3 text-xs font-semibold text-slate-500 uppercase">Commission Fee</th>
                  <th className="text-right px-4 py-3 text-xs font-semibold text-slate-500 uppercase">{t("netAmount") || "Net Amount"}</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase">{t("status")}</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase">{t("date")}</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {isLoading && !transactions.length ? (
                  Array.from({ length: pageSize }).map((_, i) => (
                    <TransactionRowSkeleton key={i} />
                  ))
                ) : transactions.length === 0 ? (
                  <tr>
                    <td colSpan={8} className="text-center py-12 text-slate-500">{t("noTransactionsFound")}</td>
                  </tr>
                ) : (
                  transactions.map((tx, index) => {
                    const statusKey = (tx.status || '').toLowerCase();
                    const status = statusConfig[statusKey] || statusConfig.pending;
                    const StatusIcon = status.icon;

                    return (
                      <motion.tr
                        key={tx.id}
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        transition={{ delay: 0.02 * index }}
                        onClick={() => setSelectedTx(tx)}
                        className="hover:bg-slate-50 transition-colors cursor-pointer"
                      >
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-3">
                            <div className={`w-9 h-9 rounded-lg flex items-center justify-center ${tx.type === "deposit" ? "bg-sky-100" : "bg-emerald-100"
                              }`}>
                              {tx.type === "deposit" ? (
                                <ArrowDownToLine className="h-4 w-4 text-sky-600" />
                              ) : (
                                <ArrowUpFromLine className="h-4 w-4 text-emerald-600" />
                              )}
                            </div>
                            <div>
                              <div className="flex items-center gap-1.5">
                                <span className="font-mono text-sm font-medium">
                                  {(tx.id || '').slice(0, 12)}...
                                </span>
                                <button
                                  onClick={(e) => copyToClipboard(e, tx.id)}
                                  className="text-slate-400 hover:text-slate-600"
                                >
                                  {copiedId === tx.id ? (
                                    <Check className="h-3.5 w-3.5 text-emerald-500" />
                                  ) : (
                                    <Copy className="h-3.5 w-3.5" />
                                  )}
                                </button>
                              </div>
                              <p className="text-xs text-slate-400">{tx.providerCode}</p>
                            </div>
                          </div>
                        </td>
                        <td className="px-4 py-3">
                          <span className="text-sm text-slate-900">{tx.merchant?.name || tx.merchant?.code || t("nA")}</span>
                        </td>
                        <td className="px-4 py-3">
                          <span className={`text-sm font-medium capitalize ${String(tx.type || "").toLowerCase() === "deposit" ? "text-sky-600" : "text-emerald-600"
                            }`}>
                              {String(tx.type || "").toLowerCase() === "deposit" ? t("deposit") : t("withdrawal")}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-right">
                          <span className="font-semibold">
                            {(() => {
                              const symbol = tx.currency === "PKR" ? "Rs" : "฿";
                              const amount = Number(tx.amount ?? 0);
                              return `${symbol}${amount.toLocaleString()}`;
                            })()}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-right">
                          <span className="font-medium text-amber-700">
                            {(() => {
                              const symbol = tx.currency === "PKR" ? "Rs" : "฿";
                              const fee = Number(tx.feeAmount ?? 0);
                              return `${symbol}${fee.toLocaleString()}`;
                            })()}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-right">
                          <span className="font-semibold text-emerald-700">
                            {(() => {
                              const symbol = tx.currency === "PKR" ? "Rs" : "฿";
                              const net = tx.netAmount ?? (Number(tx.amount ?? 0) - Number(tx.feeAmount ?? 0));
                              return `${symbol}${Number(net || 0).toLocaleString()}`;
                            })()}
                          </span>
                        </td>
                        <td className="px-4 py-3">
                          <Badge className={`${status.color} gap-1 border-0`}>
                            <StatusIcon className={`h-3 w-3 ${tx.status === "processing" ? "animate-spin" : ""}`} />
                            {status.label}
                          </Badge>
                        </td>
                        <td className="px-4 py-3 text-sm text-slate-500">
                          {tx.createdAt ? format(new Date(tx.createdAt), "MMM d, HH:mm") : t("nA")}
                        </td>
                      </motion.tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex items-center justify-between">
            <p className="text-sm text-slate-500">
              {t("showing")} {page * pageSize + 1} {t("to")} {Math.min((page + 1) * pageSize, totalTransactions)} {t("of")} {totalTransactions.toLocaleString()} {t("transactions")}
            </p>
            <div className="flex items-center gap-2">
              <Button variant="outline" size="sm" onClick={() => setPage(page - 1)} disabled={page === 0}>
                <ChevronLeft className="h-4 w-4" />
              </Button>
              <span className="text-sm text-slate-600">{t("page")} {page + 1} {t("of")} {totalPages}</span>
              <Button variant="outline" size="sm" onClick={() => setPage(page + 1)} disabled={page >= totalPages - 1}>
                <ChevronRight className="h-4 w-4" />
              </Button>
            </div>
          </div>
        )}

        {/* Transaction Detail Sheet */}
        <Sheet open={!!selectedTx} onOpenChange={() => setSelectedTx(null)}>
          <SheetContent className="w-full sm:max-w-lg overflow-y-auto">
            {selectedTx && (
              <>
                <SheetHeader className="pb-4 border-b">
                  <SheetTitle className="flex items-center gap-3">
                    <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${selectedTx.type === "deposit" ? "bg-sky-100" : "bg-emerald-100"
                      }`}>
                      {selectedTx.type === "deposit" ? (
                        <ArrowDownToLine className="h-5 w-5 text-sky-600" />
                      ) : (
                        <ArrowUpFromLine className="h-5 w-5 text-emerald-600" />
                      )}
                    </div>
                    <div>
                      <p className="text-lg capitalize">{selectedTx.type === "deposit" ? t("deposit") : t("withdrawal")}</p>
                      <Badge className={`${statusConfig[(selectedTx.status || '').toLowerCase()]?.color} border-0`}>
                        {statusConfig[(selectedTx.status || '').toLowerCase()]?.label}
                      </Badge>
                    </div>
                  </SheetTitle>
                </SheetHeader>

                <div className="py-6 space-y-6">
                  {/* Amount */}
                  <div className="text-center py-4 bg-slate-50 rounded-xl">
                    <p className="text-sm text-slate-500 mb-1">{t("amount")}</p>
                    <p className={`text-3xl font-bold ${selectedTx.type === "deposit" ? "text-sky-600" : "text-emerald-600"
                      }`}>
                      {(() => {
                        const symbol = selectedTx.currency === "PKR" ? "Rs" : "฿";
                        return `${symbol}${Number(selectedTx.amount ?? 0).toLocaleString()}`;
                      })()}
                    </p>
                    {selectedTx.feeAmount && (
                      <p className="text-sm text-slate-400 mt-1">
                        {t("fee")}: {(() => {
                          const symbol = selectedTx.currency === "PKR" ? "Rs" : "฿";
                          return `${symbol}${Number(selectedTx.feeAmount || 0).toLocaleString()}`;
                        })()}
                      </p>
                    )}
                  </div>

                  {/* Details */}
                  <div className="space-y-4">
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <p className="text-sm text-slate-500">{t("merchant")}</p>
                        <p className="font-medium">{selectedTx.merchant?.name || selectedTx.merchant?.code || t("nA")}</p>
                      </div>
                      <div>
                        <p className="text-sm text-slate-500">{t("provider")}</p>
                        <p className="font-medium">{selectedTx.providerCode || selectedTx.provider?.code}</p>
                      </div>
                      <div>
                        <p className="text-sm text-slate-500">{t("method")}</p>
                        <p className="font-medium capitalize">{selectedTx.paymentMethod?.replace(/_/g, " ")}</p>
                      </div>
                      <div>
                        <p className="text-sm text-slate-500">{t("ref")}</p>
                        <p className="font-medium">{selectedTx.providerTransactionId || t("nA")}</p>
                      </div>
                    </div>

                    {/* Transaction ID */}
                    <div>
                      <p className="text-sm text-slate-500 mb-2">{t("transactionId")}</p>
                      <div className="flex items-center gap-2">
                        <code className="flex-1 bg-slate-100 px-3 py-2 rounded-lg font-mono text-sm">
                          {selectedTx.id}
                        </code>
                        <Button variant="outline" size="icon" onClick={(e) => copyToClipboard(e, selectedTx.id)}>
                          {copiedId === selectedTx.id ? (
                            <Check className="h-4 w-4 text-emerald-500" />
                          ) : (
                            <Copy className="h-4 w-4" />
                          )}
                        </Button>
                      </div>
                    </div>

                    {/* Status Timeline */}
                    <div>
                      <p className="text-sm text-slate-500 mb-3">{t("statusTimeline")}</p>
                      <div className="space-y-3">
                        {[
                          { label: t("createdLabel"), time: selectedTx.createdAt, done: true },
                          { label: t("processingLabel"), time: selectedTx.processedAt, done: ["processing", "success", "failed"].includes(selectedTx.status) },
                          { label: selectedTx.status === "failed" || selectedTx.status === "declined" ? (t("declined") || "Declined") : (t("completedLabel") || "Completed"), time: selectedTx.completedAt, done: ["success", "failed", "declined"].includes(selectedTx.status), failed: ["failed", "declined"].includes(selectedTx.status) },
                        ].map((step, i) => (
                          <div key={i} className="flex gap-3">
                            <div className="flex flex-col items-center">
                              <div className={`w-6 h-6 rounded-full flex items-center justify-center ${step.done ? (step.failed ? "bg-red-100" : "bg-emerald-100") : "bg-slate-100"
                                }`}>
                                {step.done ? (
                                  step.failed ? (
                                    <XCircle className="h-3 w-3 text-red-600" />
                                  ) : (
                                    <CheckCircle2 className="h-3 w-3 text-emerald-600" />
                                  )
                                ) : (
                                  <Clock className="h-3 w-3 text-slate-400" />
                                )}
                              </div>
                              {i < 2 && <div className={`w-0.5 h-6 ${step.done ? "bg-emerald-200" : "bg-slate-200"}`} />}
                            </div>
                            <div>
                              <p className={`text-sm font-medium ${step.done ? "text-slate-900" : "text-slate-400"}`}>
                                {step.label}
                              </p>
                              {step.time && step.done && (
                                <p className="text-xs text-slate-500">{format(new Date(step.time), "MMM d, HH:mm:ss")}</p>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>

                    {/* Callback Status */}
                    <div>
                      <p className="text-sm text-slate-500 mb-2">{t("callbackStatus")}</p>
                      <Badge className={
                        (() => {
                          const statusLower = (selectedTx.callbackStatus || "pending").toLowerCase();
                          return statusLower === "success" || statusLower === "delivered"
                            ? "bg-emerald-100 text-emerald-700"
                            : statusLower === "failed"
                              ? "bg-red-100 text-red-700"
                              : "bg-amber-100 text-amber-700";
                        })()
                      }>
                        {(() => {
                          const status = (selectedTx.callbackStatus || "pending").toLowerCase();
                          if (status === "success" || status === "delivered") return t("delivered");
                          if (status === "failed") return t("failed");
                          return t("pending");
                        })()}
                      </Badge>
                      {selectedTx.callbackAttempts > 0 && (
                        <span className="text-sm text-slate-500 ml-2">
                          ({selectedTx.callbackAttempts} {t("attempts")})
                        </span>
                      )}
                    </div>

                    {/* Error Message */}
                    {selectedTx.errorMessage && (
                      <div className="p-4 bg-red-50 border border-red-200 rounded-xl">
                        <p className="text-sm font-medium text-red-800">{t("error")}</p>
                        <p className="text-sm text-red-700 mt-1">{selectedTx.errorMessage}</p>
                      </div>
                    )}
                  </div>
                </div>
              </>
            )}
          </SheetContent>
        </Sheet>
      </div>
    </EngineLayout>
  );
}